import { forwardRef, ReactNode } from 'react';

import List, { ListProps } from '@mui/material/List';
import { styled, useThemeProps } from '@mui/material/styles';

export interface GratiDirectoryProps extends ListProps {
  children?: ReactNode;
  sticky?: boolean;
  /** Number of columns at md+ breakpoint. Defaults to 1 (single column). Use 2 for most list pages. */
  columns?: 1 | 2 | 3;
  /**
   * What the sticky subheader must clear as it scrolls: the AppBar alone (default), or a
   * GratiTabBar rendered directly above this directory. Only set 'tabBar' when a GratiTabBar is
   * actually present above — otherwise the subheader sticks with an unexplained gap under the AppBar.
   */
  stickyTopOffset?: 'appBar' | 'tabBar';
}

interface GratiDirectoryOwnerState extends GratiDirectoryProps {
  columns: 1 | 2 | 3;
}

const GratiDirectoryRoot = styled(List, {
  name: 'GratiDirectory',
  slot: 'root',
  shouldForwardProp: (prop) => prop !== 'ownerState' && prop !== 'sticky' && prop !== 'stickyTopOffset',
  overridesResolver: (_props, styles) => {
    return styles.root;
  },
})<{ ownerState: GratiDirectoryOwnerState }>(() => ({}));

export default forwardRef<HTMLUListElement, GratiDirectoryProps>(function GratiDirectory(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'GratiDirectory' });

  const {
    children,
    columns = 1,
    subheader,
    sx,
    ...other
  } = props;

  const ownerState: GratiDirectoryOwnerState = { ...props, columns };

  return (
    <GratiDirectoryRoot
      ref={ref}
      ownerState={ownerState}
      subheader={subheader}
      sx={{

        ...sx
      }}
      {...other}
    >
      {children}
    </GratiDirectoryRoot>
  );
});
